import React, { useState, useEffect } from 'react';
import get from 'lodash/get';
import { useTranslation } from 'react-i18next';

import ListResource from './ListResource.jsx';
import BtnToggle from '~/commons/components/BtnToggle.jsx';
import BtnDayPicker from '../btns/BtnDayPicker.jsx';
import { env } from '../../config';
import { getLang } from '~/commons/helpers';
import useHttpStatus from '~/commons/hooks/useHttpStatus';

const isAdmin = env.user.roles.includes('admin');

const ShowStudentRankingSwitch = () => {
  const { t, i18n: { language } } = useTranslation();
  const [checked, setChecked] = useState(null);
  const [res, req] = useHttpStatus();

  useEffect(() => {
    req({ url: '/api/core/schoolclasses/showRankings' });
  }, []);

  useEffect(() => {
    if (res.status == 'success') {
      setChecked(res.payload == true);
    }
  }, [res.status, res.payload]);

  if (checked === null) {
    return null;
  }

  return (
    <div className="inline-block float-right">
      <div className="form-group">
        <label className="pr-3" htmlFor="showSchoolClassRankings">{t('Show Student Page Class Rankings')}</label>
        <div className="d-inline-block custom-control custom-switch">
          <input
            className="custom-control-input"
            type="checkbox"
            role="switch"
            id="showSchoolClassRankings"
            checked={checked}
            onChange={(evt) => {
              // console.log('onChannge', evt.target.checked)
              // setChecked(evt.target.checked);
              req({
                url: '/api/core/schoolclasses/updateShowRankings',
                method: 'patch',
                data: { hideRanking: !evt.target.checked },
              });
            }}
          />
          <label className="custom-control-label" htmlFor="showSchoolClassRankings"></label>
        </div>
      </div>
    </div>
  );
};

let withins = [
  { key: 'school', label: 'School' },
  { key: 'district', label: 'District' },
  { key: 'all', label: 'Hong Kong' },
];

if (!isAdmin) {
  withins = withins.filter((w) => w.key !== 'district');
}

const api = '/api/quiz/rankings';

const ListRankings = () => {
  const stage = env?.school?.stage;
  const [date, setDate] = useState(null);
  const [group, setGroup] = useState({
    unitType: 'student',
    within: 'all',
  });
  const [district, setDistrict] = useState('');
  const { unitType, within } = group;
  const { t, i18n: { language } } = useTranslation();
  const lang = language === 'en' ? 'en' : 'zhhk';

  const filter = {};

  if (district) filter.district = district;
  if (date) filter.archive = date;

  return (
    <>
      <div className="form-row my-3">
        <div className="col">
          <BtnToggle
            className="btn-block btn-outline-tertiary"
            value={unitType === 'student'}
            onChange={() => setGroup({
              unitType: 'student',
              within,
            })}
          >
            {t('Personal')}
          </BtnToggle>
        </div>
        {/* Group toggle removed (schoolClass scope disabled) */}
      </div>
      <div className="form-row my-3">
        {withins.filter(((w) => w.key !== unitType)).map((w) => (
          <div key={w.key} className="col">
            <BtnToggle
              className="btn-block btn-outline-tertiary"
              value={within === w.key}
              onChange={() => {
                if (w.key !== 'district') setDistrict('');

                setGroup({
                  within: w.key,
                  unitType,
                });
              }}
            >
              {t(w.label)}
            </BtnToggle>
          </div>
        ))}
      </div>
      <ListResource
        {...{
          searchable: unitType === 'schoolClass'
            ? [
              { value: 'unit.title', label: 'Class' },
              {
                value: 'unit.grade',
                label: 'Grade',
                options: Object.keys(env.grades).map((g) => ({
                  value: g,
                  label: env.grades[g][lang],
                })),
              },
            ]
            : [
              { value: 'unit.name', label: 'Name' },
              { value: 'unit.schoolClass.title', label: 'Class' },
              {
                value: 'unit.schoolClass.grade',
                label: 'Grade',
                options: Object.keys(env.grades)
                  .filter((g) => {
                    if (stage === 'p') return g <= 6;
                    if (stage === 's') return g >= 7;

                    return true;
                  })
                  .map((g) => ({
                    value: g,
                    label: env.grades[g][lang],
                  })),
              },
              { value: 'unit.studentId', label: 'Class No.' },
              ...isAdmin ? [{
                value: 'school.title', label: 'School',
              }] : [],
            ],
          api: `${api}/${unitType}/${within}`,
          sort: { rank: 1 },
          filter,
          actions: {
            fileExport: true,
            find: false,
            create: false,
            remove: false,
          },
          cols: [
            {
              key: 'rank',
              sortable: true,
              getter: (v) => v + 1,
            },
            {
              key: 'score',
              children: 'Total Score',
              sortable: true,
            },
            {
              key: 'lv',
              children: 'Group',
              sortable: true,
              getter: (v) => env.lvs[v]?.[lang] || '',
            },
            ...(
              unitType === 'student'
                ? [
                  {
                    key: 'unit.name.full.en',
                    children: 'Name',
                    sortable: true,
                    getter: (v, r) => getLang(get(r, 'unit.name.full'), lang),
                  },
                  {
                    key: 'unit.schoolClass.title',
                    children: 'Class',
                    sortable: true,
                  },
                  {
                    key: 'unit.studentNo',
                    children: 'Class No.',
                    sortable: true,
                    getter: (v, r) => r?.unit?.studentId,
                  },
                ]
                : [
                  {
                    key: 'unit.title',
                    children: 'Class',
                    sortable: true,
                  },
                ]
            ),
            ...(env.user.roles.includes('admin') ? [
              {
                key: 'unit.school.title.zhhk',
                children: 'School',
                getter: (v, r) => getLang(get(r, 'unit.school.title'), lang),
              },
              {
                key: 'unit.school.district',
                children: 'District',
                getter: (v) => env.districts[v][lang],
              },
            ] : []),
          ],
          ctrls: within === 'district' ? [
            <select
              className="custom-select"
              key={0}
              onChange={(e) => setDistrict(e.target.value)}
            >
              <option value="">{t('Show All')}</option>
              {Object.keys(env.districts).map((d) => (
                <option key={d} value={d}>
                  {env.districts[d][lang]}
                </option>
              ))}
            </select>,
          ] : [],
        }}
      >
        {() => (
        <>
          <BtnDayPicker
            className="mr-2"
            value={date}
            onChange={setDate}
            placeholder={t('Date')}
          />
          {env.user.roles.includes('schoolAdmin') && (
          <ShowStudentRankingSwitch />
          )}
        </>
        )}
      </ListResource>
    </>
  );
};

export default ListRankings;
